/**
 * Regional Obligations Page — M6.3 Phase 1
 * Browse regional governance obligations and notification rules across 10 global regions
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { API, useAuth } from '../App';
import QMSLayout from '../components/QMSLayout';
import {
  Globe, Shield, Bell, FileText, ChevronDown, ChevronUp,
  Loader2, AlertTriangle, MapPin, ShieldAlert, Lock,
  Database, Server, AlertCircle, ExternalLink
} from 'lucide-react';
import { OBLIGATION_CATEGORY_CONFIG } from '../constants/registry';

const REGION_FLAGS = {
  US: 'US', EU: 'EU', CA: 'CA', BR: 'BR', LATAM: 'LATAM',
  AU: 'AU', JP: 'JP', CN: 'CN', UK: 'UK', OUS: 'OUS',
};

// Icons are a UI concern — compose with shared registry data
const CATEGORY_ICONS = {
  incident_notification: Bell,
  data_residency: Server,
  privacy_security: Lock,
  record_integrity: Database,
  breach_reporting: AlertCircle,
};
const CATEGORY_CONFIG = Object.fromEntries(
  Object.entries(OBLIGATION_CATEGORY_CONFIG).map(([k, v]) => [k, { ...v, icon: CATEGORY_ICONS[k] }])
);

const ObligationCard = ({ obligation }) => {
  const [expanded, setExpanded] = useState(false);
  const catCfg = CATEGORY_CONFIG[obligation.category] || CATEGORY_CONFIG.incident_notification;
  const CatIcon = catCfg.icon;

  return (
    <div className="qms-card" data-testid={`obligation-card-${obligation.obligation_id}`}>
      <div className="qms-card-body p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded ${catCfg.bg}`}>
              <CatIcon className={catCfg.color} size={14} />
            </div>
            <div>
              <h4 className="text-white text-sm font-medium">{obligation.title}</h4>
              <span className="text-[#8B95A5] text-xs">{obligation.reference_label}</span>
            </div>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded ${catCfg.bg} ${catCfg.color} font-medium shrink-0`}>
            {catCfg.label}
          </span>
        </div>

        <p className="text-[#8B95A5] text-xs leading-relaxed mb-2">{obligation.description}</p>

        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-[#36A9E1] text-xs hover:text-[#38BDF8] transition-colors"
          data-testid={`obligation-expand-${obligation.obligation_id}`}
        >
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          {expanded ? 'Hide notes' : 'Show notes'}
        </button>

        {expanded && (
          <div className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.05)] space-y-3">
            {obligation.data_residency_notes && (
              <div>
                <span className="text-[#8B95A5] text-xs font-medium block mb-1">Data Residency / Transfer Notes:</span>
                <p className="text-[#B8C7D9] text-xs leading-relaxed bg-[rgba(255,255,255,0.03)] p-2 rounded">{obligation.data_residency_notes}</p>
              </div>
            )}
            {obligation.notification_notes && (
              <div>
                <span className="text-[#8B95A5] text-xs font-medium block mb-1">Notification Considerations:</span>
                <p className="text-[#B8C7D9] text-xs leading-relaxed bg-[rgba(255,255,255,0.03)] p-2 rounded">{obligation.notification_notes}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const RegionPanel = ({ region, obligations }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="qms-card" data-testid={`region-panel-${region.region}`}>
      <div
        className="qms-card-body p-4 cursor-pointer hover:bg-[rgba(255,255,255,0.02)] transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[rgba(54,169,225,0.1)] flex items-center justify-center text-[#36A9E1] font-mono text-xs font-bold">
              {REGION_FLAGS[region.region]}
            </div>
            <div>
              <h3 className="text-white font-medium text-sm" data-testid={`region-label-${region.region}`}>{region.label}</h3>
              <span className="text-[#8B95A5] text-xs">{region.obligation_count} obligations tracked</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[#8B95A5] text-xs">{expanded ? 'Collapse' : 'Expand'}</span>
            {expanded ? <ChevronUp className="text-[#8B95A5]" size={16} /> : <ChevronDown className="text-[#8B95A5]" size={16} />}
          </div>
        </div>
      </div>

      {expanded && obligations && (
        <div className="border-t border-[rgba(255,255,255,0.05)] p-4 space-y-3">
          {obligations.map(o => <ObligationCard key={o.obligation_id} obligation={o} />)}
          {obligations.length === 0 && (
            <p className="text-[#8B95A5] text-xs text-center py-4">No obligations recorded for this region.</p>
          )}
        </div>
      )}
    </div>
  );
};

const NotificationRulesPanel = ({ rules }) => {
  if (!rules || rules.length === 0) return null;
  const grouped = {};
  rules.forEach(r => {
    if (!grouped[r.region]) grouped[r.region] = [];
    grouped[r.region].push(r);
  });

  return (
    <div className="qms-card mt-6" data-testid="notification-rules-panel">
      <div className="qms-card-body p-5">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="text-amber-400" size={20} />
          <h2 className="text-white font-semibold text-sm">Incident Notification Rules</h2>
          <span className="text-[#8B95A5] text-xs ml-2">{rules.length} rules across {Object.keys(grouped).length} regions</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="notification-rules-table">
            <thead>
              <tr className="border-b border-[rgba(255,255,255,0.07)]">
                <th className="text-left text-[#8B95A5] font-medium px-3 py-2">Region</th>
                <th className="text-left text-[#8B95A5] font-medium px-3 py-2">Category</th>
                <th className="text-left text-[#8B95A5] font-medium px-3 py-2">Trigger</th>
                <th className="text-left text-[#8B95A5] font-medium px-3 py-2">Description</th>
                <th className="text-left text-[#8B95A5] font-medium px-3 py-2">Owner</th>
              </tr>
            </thead>
            <tbody>
              {rules.map(r => (
                <tr key={r.rule_id} className="border-b border-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.02)]">
                  <td className="text-[#36A9E1] font-mono px-3 py-2">{r.region}</td>
                  <td className="text-white px-3 py-2 capitalize">{r.category.replace(/_/g, ' ')}</td>
                  <td className="text-[#B8C7D9] px-3 py-2 capitalize">{r.trigger_type.replace(/_/g, ' ')}</td>
                  <td className="text-[#8B95A5] px-3 py-2 max-w-md">{r.description}</td>
                  <td className="text-[#B8C7D9] px-3 py-2">{r.owner_role}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const RegionalObligationsPage = () => {
  const { t } = useTranslation(['common']);
  const { isAdmin, isAuditor } = useAuth();

  const [regions, setRegions] = useState([]);
  const [obligations, setObligations] = useState({});
  const [notificationRules, setNotificationRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [regRes, oblRes, nrRes] = await Promise.all([
        fetch(`${API}/governance/regions`, { credentials: 'include' }),
        fetch(`${API}/governance/regions/obligations/all`, { credentials: 'include' }),
        fetch(`${API}/governance/regions/notifications/rules`, { credentials: 'include' }),
      ]);
      if (regRes.status === 403) {
        setError('Access denied. This module is restricted to governance roles.');
        return;
      }
      if (!regRes.ok || !oblRes.ok || !nrRes.ok) throw new Error('Failed to load data');
      const [regData, oblData, nrData] = await Promise.all([
        regRes.json(), oblRes.json(), nrRes.json(),
      ]);

      setRegions(regData);
      setNotificationRules(nrData);

      // Group obligations by region
      const grouped = {};
      oblData.forEach(o => {
        if (!grouped[o.region]) grouped[o.region] = [];
        grouped[o.region].push(o);
      });
      setObligations(grouped);
    } catch (err) {
      console.error('Error loading regional obligations:', err);
      setError('Failed to load regional obligations.');
    } finally {
      setLoading(false);
    }
  };

  const filteredObligations = (regionObl) => {
    if (categoryFilter === 'all') return regionObl || [];
    return (regionObl || []).filter(o => o.category === categoryFilter);
  };

  if (!isAdmin && !isAuditor) {
    return (
      <QMSLayout title={t('governance:regional.title')}>
        <div className="qms-card">
          <div className="qms-card-body p-8 text-center">
            <Shield className="mx-auto mb-4 text-red-400" size={48} />
            <h3 className="text-white font-semibold mb-2">Access Restricted</h3>
            <p className="text-[#8B95A5] text-sm">This module is restricted to governance roles (Admin, Auditor).</p>
          </div>
        </div>
      </QMSLayout>
    );
  }

  if (loading) {
    return (
      <QMSLayout title={t('governance:regional.title')}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
        </div>
      </QMSLayout>
    );
  }

  return (
    <QMSLayout title={t('governance:regional.title')}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Globe className="text-[#36A9E1]" size={28} />
          <h1 className="font-['Syne'] text-2xl font-bold text-white" data-testid="regional-obligations-title">Regional Obligations Matrix</h1>
        </div>
        <p className="text-[#8B95A5] text-sm">
          Governance obligations, notification readiness, and data transfer considerations across global jurisdictions
        </p>
      </div>

      {/* Region Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="region-summary-grid">
        {regions.map(r => (
          <div key={r.region} className="qms-card">
            <div className="qms-card-body p-3 text-center">
              <div className="text-[#36A9E1] font-mono text-xs font-bold mb-1">{REGION_FLAGS[r.region]}</div>
              <div className="text-white text-xs font-medium truncate">{r.label}</div>
              <div className="text-[#8B95A5] text-xs mt-0.5">{r.obligation_count} obligations</div>
            </div>
          </div>
        ))}
      </div>

      {/* Governance Posture Notice */}
      <div className="qms-card border-[rgba(54,169,225,0.2)] mb-6">
        <div className="qms-card-body p-4">
          <div className="flex items-start gap-3">
            <ShieldAlert className="text-[#36A9E1] shrink-0 mt-0.5" size={20} />
            <div>
              <h4 className="text-[#36A9E1] font-medium text-sm mb-1">Regional Governance Awareness</h4>
              <p className="text-[#8B95A5] text-xs leading-relaxed">
                This matrix tracks governance considerations per region for assessment and readiness planning. It does not constitute legal advice. Organizations should consult qualified legal and regulatory professionals for jurisdiction-specific compliance decisions.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="qms-card mb-6">
        <div className="qms-card-body p-4">
          <div className="flex items-center gap-4">
            <span className="text-[#8B95A5] text-xs font-medium">Filter by category:</span>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 bg-[#0B0E14] border border-[rgba(255,255,255,0.07)] rounded text-white text-sm focus:border-[#36A9E1] focus:outline-none"
              data-testid="obligations-category-filter"
            >
              <option value="all">All Categories</option>
              {Object.entries(CATEGORY_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="qms-card border-red-500/30 mb-6">
          <div className="qms-card-body p-4 text-red-400 text-sm flex items-center gap-2">
            <AlertTriangle size={16} />
            {error}
          </div>
        </div>
      )}

      {/* Region Panels */}
      <div className="space-y-3" data-testid="regions-list">
        {regions.map(r => (
          <RegionPanel
            key={r.region}
            region={r}
            obligations={filteredObligations(obligations[r.region])}
          />
        ))}
      </div>

      {/* Notification Rules Table */}
      <NotificationRulesPanel rules={notificationRules} />

      {/* Footer */}
      <div className="mt-4 text-center text-[#8B95A5] text-xs">
        {regions.length} regions tracked | {Object.values(obligations).flat().length} obligations | {notificationRules.length} notification rules
      </div>
    </QMSLayout>
  );
};

export default RegionalObligationsPage;
